import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRight, Eye, EyeOff, Loader2, Search, X } from 'lucide-react';
import { COND_TIPO_OS_FIELDS } from '../../process-config/components/condicoesIntervShared';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { DASH_VISIBILITY_KEYS, normalizeDashboardVisibility } from './permDashVisibility';
import './PermDisponibilidadeIndexEditor.css';

const API_BASE_URL = getApiBaseUrl();
const MAX_STATUS = 9;

function normStatusCode(v) {
  return String(v || '')
    .trim()
    .toUpperCase()
    .slice(0, 2);
}

function normalizeAvailabilityDraft(src) {
  const ai = src?.availabilityIndex || src || {};
  return {
    defectCodes: Array.isArray(ai.defectCodes)
      ? ai.defectCodes.map((c) => String(c).trim()).filter(Boolean)
      : [],
    statusCodes: Array.isArray(ai.statusCodes)
      ? ai.statusCodes.map(normStatusCode).filter((c) => c.length === 2)
      : [],
    osTypeKeys: Array.isArray(ai.osTypeKeys)
      ? ai.osTypeKeys.map((k) => String(k).trim()).filter(Boolean)
      : [],
  };
}

function StatusPill({ tag, index, onRemove, onUpdate }) {
  const isDraft = tag === '';
  const [editing, setEditing] = useState(isDraft);
  const [draft, setDraft] = useState(tag);
  const editRef = useRef(null);

  useEffect(() => {
    setDraft(tag);
    if (tag === '') setEditing(true);
  }, [tag]);

  useEffect(() => {
    if (editing) editRef.current?.focus();
  }, [editing]);

  const commitEdit = useCallback(() => {
    const trimmed = draft.trim().toUpperCase();
    if (trimmed.length === 2) {
      onUpdate(index, trimmed);
      setEditing(false);
    } else if (!trimmed) {
      onRemove(index);
    } else {
      setDraft(tag);
      setEditing(false);
    }
  }, [draft, index, onRemove, onUpdate, tag]);

  return (
    <span className={`pct-ai-status-pill${isDraft ? ' is-draft' : ''}`}>
      {editing ? (
        <input
          ref={editRef}
          type="text"
          className="pct-ai-status-code pct-ai-status-code--edit"
          value={draft}
          maxLength={2}
          placeholder="00"
          onChange={(e) => setDraft(e.target.value.toUpperCase())}
          onBlur={commitEdit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitEdit();
            }
            if (e.key === 'Escape') {
              if (isDraft) onRemove(index);
              else {
                setDraft(tag);
                setEditing(false);
              }
            }
          }}
          aria-label="Editar status"
        />
      ) : (
        <button
          type="button"
          className="pct-ai-status-code"
          onClick={() => {
            setDraft(tag);
            setEditing(true);
          }}
          aria-label={`Editar status ${tag}`}
        >
          {tag}
        </button>
      )}
      <button
        type="button"
        className="pct-ai-status-remove"
        onClick={() => onRemove(index)}
        aria-label={`Remover status ${tag || 'novo'}`}
      >
        ×
      </button>
    </span>
  );
}

function DefectPickerModal({
  open,
  onClose,
  defects,
  loading,
  error,
  selectedCodes,
  onToggle,
  busy = false,
}) {
  const [filter, setFilter] = useState('');
  const searchRef = useRef(null);
  const selectedSet = useMemo(() => new Set(selectedCodes), [selectedCodes]);

  useEffect(() => {
    if (!open) return;
    setFilter('');
    const t = window.setTimeout(() => searchRef.current?.focus(), 40);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose?.();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return defects;
    return defects.filter((d) => {
      const cod = String(d.TB01048_CODIGO || '').toLowerCase();
      const nome = String(d.TB01048_NOME || '').toLowerCase();
      return cod.includes(q) || nome.includes(q);
    });
  }, [defects, filter]);

  const selectedRows = useMemo(
    () => filtered.filter((d) => selectedSet.has(String(d.TB01048_CODIGO || '').trim())),
    [filtered, selectedSet]
  );
  const availableRows = useMemo(
    () => filtered.filter((d) => !selectedSet.has(String(d.TB01048_CODIGO || '').trim())),
    [filtered, selectedSet]
  );

  if (!open) return null;

  const renderRow = (d) => {
    const cod = String(d.TB01048_CODIGO || '').trim();
    const nome = String(d.TB01048_NOME || '').trim();
    const on = selectedSet.has(cod);
    return (
      <button
        key={cod}
        type="button"
        role="option"
        aria-selected={on}
        className={`pct-ai-defect-row${on ? ' is-on' : ''}`}
        onClick={() => onToggle(cod)}
        disabled={busy}
      >
        <span className={`pct-ai-check${on ? ' is-on' : ''}`} aria-hidden />
        <span className="pct-ai-defect-cod">{cod}</span>
        <span className="pct-ai-defect-nome">{nome}</span>
      </button>
    );
  };

  return createPortal(
    <div
      className="pct-ai-defect-modal-overlay"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="pct-ai-defect-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Selecionar defeitos"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="pct-ai-defect-modal-head">
          <div>
            <h3 className="pct-ai-defect-modal-title">Defeitos do índice</h3>
            <p className="pct-ai-defect-modal-sub">
              {selectedCodes.length} selecionado{selectedCodes.length === 1 ? '' : 's'}
            </p>
          </div>
          <button type="button" className="pct-ai-editor-close" aria-label="Fechar" onClick={onClose}>
            <X size={16} strokeWidth={2} />
          </button>
        </header>

        <div className="pct-ai-defect-modal-search">
          <Search size={14} strokeWidth={2} aria-hidden />
          <input
            ref={searchRef}
            type="search"
            className="pct-ai-search pct-ai-search--modal"
            placeholder="Pesquisar código ou nome…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            disabled={loading || busy}
          />
        </div>

        <div className="pct-ai-defect-modal-body">
          {loading ? (
            <div className="pct-ai-empty">
              <Loader2 size={14} className="pct-ai-spin" aria-hidden />
              Carregando defeitos…
            </div>
          ) : error ? (
            <div className="pct-ai-empty pct-ai-empty--err">{error}</div>
          ) : filtered.length === 0 ? (
            <div className="pct-ai-empty">Nenhum defeito encontrado.</div>
          ) : (
            <div className="pct-ai-defect-modal-cols">
              <section className="pct-ai-defect-modal-section">
                <h4 className="pct-ai-defect-modal-sec-title">
                  Disponíveis <em>({availableRows.length})</em>
                </h4>
                <div className="pct-ai-defect-list pct-ai-defect-list--modal" role="listbox">
                  {availableRows.length === 0 ? (
                    <div className="pct-ai-empty-inline">Nenhum disponível nesta pesquisa</div>
                  ) : (
                    availableRows.map(renderRow)
                  )}
                </div>
              </section>
              <section className="pct-ai-defect-modal-section">
                <h4 className="pct-ai-defect-modal-sec-title">
                  Selecionados <em>({selectedRows.length})</em>
                </h4>
                <div className="pct-ai-defect-list pct-ai-defect-list--modal" role="listbox">
                  {selectedRows.length === 0 ? (
                    <div className="pct-ai-empty-inline">Nenhum selecionado nesta pesquisa</div>
                  ) : (
                    selectedRows.map(renderRow)
                  )}
                </div>
              </section>
            </div>
          )}
        </div>

        <footer className="pct-ai-defect-modal-foot">
          <button
            type="button"
            className="user-form-profile-save-btn user-form-footer-btn"
            onClick={onClose}
          >
            Concluído
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
}

/**
 * Painel: o que é exibido no dashboard + config do Índice de disponibilidade.
 */
export default function PermDisponibilidadeIndexEditor({
  open,
  onClose,
  techCallCfg,
  onSave,
  busy = false,
}) {
  const [draft, setDraft] = useState(() => normalizeAvailabilityDraft(techCallCfg));
  const [visibilityDraft, setVisibilityDraft] = useState(() =>
    normalizeDashboardVisibility(techCallCfg)
  );
  const [defects, setDefects] = useState([]);
  const [loadingDefects, setLoadingDefects] = useState(false);
  const [defectsError, setDefectsError] = useState(null);
  const [defectModalOpen, setDefectModalOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setDefectModalOpen(false);
      return;
    }
    setDraft(normalizeAvailabilityDraft(techCallCfg));
    setVisibilityDraft(normalizeDashboardVisibility(techCallCfg));
  }, [open, techCallCfg]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      setLoadingDefects(true);
      setDefectsError(null);
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`${API_BASE_URL}/api/config/defeitos-verve`, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
        });
        const j = await res.json().catch(() => ({}));
        if (cancelled) return;
        const rows = Array.isArray(j.data) ? j.data : [];
        setDefects(
          rows.filter((r) => r.habilitado === 1 || r.habilitado === true || r.habilitado === '1')
        );
      } catch (e) {
        if (!cancelled) {
          setDefects([]);
          setDefectsError(e.message || 'Falha ao carregar defeitos');
        }
      } finally {
        if (!cancelled) setLoadingDefects(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const selectedOsSet = useMemo(() => new Set(draft.osTypeKeys), [draft.osTypeKeys]);

  const selectedDefectPreview = useMemo(() => {
    return draft.defectCodes
      .map((cod) => {
        const row = defects.find((d) => String(d.TB01048_CODIGO || '').trim() === cod);
        return {
          cod,
          nome: row ? String(row.TB01048_NOME || '').trim() : '',
        };
      })
      .slice(0, 3);
  }, [draft.defectCodes, defects]);

  const toggleDefect = (cod) => {
    const c = String(cod).trim();
    setDraft((prev) => {
      const has = prev.defectCodes.includes(c);
      return {
        ...prev,
        defectCodes: has ? prev.defectCodes.filter((x) => x !== c) : [...prev.defectCodes, c],
      };
    });
  };

  const toggleOsType = (key) => {
    setDraft((prev) => {
      const has = prev.osTypeKeys.includes(key);
      return {
        ...prev,
        osTypeKeys: has ? prev.osTypeKeys.filter((x) => x !== key) : [...prev.osTypeKeys, key],
      };
    });
  };

  const toggleVisibilityKey = (key) => {
    setVisibilityDraft((prev) => ({
      ...prev,
      [key]: prev[key] === 1 ? 0 : 1,
    }));
  };

  const addStatusEmpty = () => {
    setDraft((prev) => {
      if (prev.statusCodes.length >= MAX_STATUS) return prev;
      if (prev.statusCodes.some((t) => t === '')) return prev;
      return { ...prev, statusCodes: [...prev.statusCodes, ''] };
    });
  };

  const removeStatus = (index) => {
    setDraft((prev) => ({
      ...prev,
      statusCodes: prev.statusCodes.filter((_, i) => i !== index),
    }));
  };

  const updateStatus = (index, next) => {
    const code = normStatusCode(next);
    setDraft((prev) => {
      const nextCodes = prev.statusCodes.map((t, i) => (i === index ? code : t));
      const seen = new Set();
      const dedup = [];
      for (const c of nextCodes) {
        if (!c) {
          dedup.push('');
          continue;
        }
        if (seen.has(c)) continue;
        seen.add(c);
        dedup.push(c);
      }
      return { ...prev, statusCodes: dedup };
    });
  };

  const handleSave = async () => {
    const clean = {
      defectCodes: draft.defectCodes.filter(Boolean),
      statusCodes: draft.statusCodes.map(normStatusCode).filter((c) => c.length === 2),
      osTypeKeys: draft.osTypeKeys.filter((k) => COND_TIPO_OS_FIELDS.some((f) => f.db === k)),
    };
    const nextCfg = {
      ...(techCallCfg && typeof techCallCfg === 'object' ? techCallCfg : {}),
      dashboardVisibility: { ...visibilityDraft },
      availabilityIndex: clean,
    };
    await onSave?.(nextCfg);
  };

  if (!open) return null;

  const atMaxStatus = draft.statusCodes.length >= MAX_STATUS;
  const hasDraftStatus = draft.statusCodes.some((t) => t === '');
  const defectCount = draft.defectCodes.length;

  return (
    <aside className="pct-ai-editor" aria-label="Configurar o que é exibido">
      <header className="pct-ai-editor-head">
        <div className="pct-ai-editor-head-copy">
          <h3 className="pct-ai-editor-title">Configurar o que é exibido</h3>
          <p className="pct-ai-editor-sub">Visibilidade dos blocos e índice de disponibilidade</p>
        </div>
        <button type="button" className="pct-ai-editor-close" aria-label="Fechar edição" onClick={onClose}>
          <X size={16} strokeWidth={2} />
        </button>
      </header>

      <div className="pct-ai-editor-body">
        <section className="pct-ai-block">
          <div className="pct-ai-block-head">
            <h4 className="pct-ai-block-title">Blocos do dashboard</h4>
            <p className="pct-ai-block-desc">Exibir ou ocultar cada elemento (0 / 1 no contrato)</p>
          </div>
          <ul className="pct-ai-vis-list" aria-label="Visibilidade dos blocos">
            {DASH_VISIBILITY_KEYS.map(({ key, label }) => {
              const on = visibilityDraft[key] === 1;
              const Icon = on ? Eye : EyeOff;
              return (
                <li key={key}>
                  <button
                    type="button"
                    className={`pct-ai-vis-row${on ? ' is-on' : ' is-off'}`}
                    onClick={() => toggleVisibilityKey(key)}
                    disabled={busy}
                    aria-pressed={on}
                  >
                    <span className="pct-ai-vis-label">{label}</span>
                    <span className="pct-ai-vis-eye" aria-hidden>
                      <Icon size={15} strokeWidth={2} />
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>

        <>
            <section className="pct-ai-block">
              <div className="pct-ai-block-head">
                <h4 className="pct-ai-block-title">Índice — Defeito</h4>
                <p className="pct-ai-block-desc">Defeitos habilitados no portal (Defeitos Verve)</p>
              </div>
              <button
                type="button"
                className="pct-ai-defect-open-btn"
                onClick={() => setDefectModalOpen(true)}
                disabled={busy}
              >
                <span className="pct-ai-defect-open-main">
                  <strong>
                    {defectCount === 0
                      ? 'Selecionar defeitos'
                      : `${defectCount} defeito${defectCount === 1 ? '' : 's'} selecionado${defectCount === 1 ? '' : 's'}`}
                  </strong>
                  {selectedDefectPreview.length > 0 ? (
                    <span className="pct-ai-defect-open-preview">
                      {selectedDefectPreview
                        .map((p) => (p.nome ? `${p.cod} · ${p.nome}` : p.cod))
                        .join(' · ')}
                      {defectCount > selectedDefectPreview.length ? '…' : ''}
                    </span>
                  ) : (
                    <span className="pct-ai-defect-open-preview">Abrir pesquisa e marcar itens</span>
                  )}
                </span>
                <ChevronRight size={16} strokeWidth={2} aria-hidden />
              </button>
              {loadingDefects ? (
                <p className="pct-ai-hint">
                  <Loader2 size={12} className="pct-ai-spin" aria-hidden /> Carregando lista…
                </p>
              ) : defectsError ? (
                <p className="pct-ai-hint pct-ai-hint--err">{defectsError}</p>
              ) : null}
            </section>

            <section className="pct-ai-block">
              <div className="pct-ai-block-head pct-ai-block-head--row">
                <div>
                  <h4 className="pct-ai-block-title">Dedução de tempo em status</h4>
                  <p className="pct-ai-block-desc">Até {MAX_STATUS} status (código de 2 caracteres)</p>
                </div>
                <button
                  type="button"
                  className="pct-ai-add-btn"
                  disabled={busy || atMaxStatus || hasDraftStatus}
                  onClick={addStatusEmpty}
                >
                  + Add
                </button>
              </div>
              <div className="pct-ai-status-chips">
                {draft.statusCodes.length === 0 ? (
                  <span className="pct-ai-empty-inline">Nenhum status — clique em + Add</span>
                ) : (
                  draft.statusCodes.map((tag, index) => (
                    <StatusPill
                      key={`st-${index}-${tag || 'draft'}`}
                      tag={tag}
                      index={index}
                      onRemove={removeStatus}
                      onUpdate={updateStatus}
                    />
                  ))
                )}
              </div>
            </section>

            <section className="pct-ai-block">
              <div className="pct-ai-block-head">
                <h4 className="pct-ai-block-title">Tipo de OS</h4>
                <p className="pct-ai-block-desc">Tipos considerados no índice</p>
              </div>
              <div className="pct-ai-os-grid" role="listbox" aria-label="Tipos de OS">
                {COND_TIPO_OS_FIELDS.map((f) => {
                  const on = selectedOsSet.has(f.db);
                  return (
                    <button
                      key={f.db}
                      type="button"
                      role="option"
                      aria-selected={on}
                      className={`pct-ai-os-chip${on ? ' is-on' : ''}`}
                      onClick={() => toggleOsType(f.db)}
                      disabled={busy}
                    >
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </section>
        </>
      </div>

      <footer className="pct-ai-editor-foot">
        <button
          type="button"
          className="user-form-link-btn user-form-footer-btn"
          onClick={onClose}
          disabled={busy}
        >
          Cancelar
        </button>
        <button
          type="button"
          className="user-form-profile-save-btn user-form-footer-btn"
          onClick={handleSave}
          disabled={busy}
        >
          {busy ? (
            <>
              <Loader2 size={14} className="pct-ai-spin" aria-hidden />
              Salvando…
            </>
          ) : (
            'Salvar'
          )}
        </button>
      </footer>

      <DefectPickerModal
        open={defectModalOpen}
        onClose={() => setDefectModalOpen(false)}
        defects={defects}
        loading={loadingDefects}
        error={defectsError}
        selectedCodes={draft.defectCodes}
        onToggle={toggleDefect}
        busy={busy}
      />
    </aside>
  );
}

export function isAvailabilityIndexConfigured(techCallCfg) {
  const ai = techCallCfg?.availabilityIndex;
  if (!ai || typeof ai !== 'object') return false;
  return (
    (Array.isArray(ai.defectCodes) && ai.defectCodes.length > 0) ||
    (Array.isArray(ai.statusCodes) && ai.statusCodes.length > 0) ||
    (Array.isArray(ai.osTypeKeys) && ai.osTypeKeys.length > 0)
  );
}
